import moment from 'moment';

export const cloneValue = (value) => {
  if (value === null || value === undefined) return value;

  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

export const normalizeBusinessPoints = (pointsList = []) => {
  let firstBusinessPointFound = false;

  return (pointsList || []).map((point) => {
    if (!point) return point;

    if (point?.type?.id === 'via' || point?.action?.id === 'via') {
      return point;
    }

    if (!firstBusinessPointFound) {
      firstBusinessPointFound = true;
      return {
        ...point,
        type: { id: 'collect', name: 'Coleta' },
        action: { id: 'collect', name: 'Coleta' },
      };
    }

    return point;
  });
};

export const parseDateForInput = (dateString) => {
  if (!dateString) return '';

  const date = moment(dateString, [
    'YYYY-MM-DDTHH:mm',
    'DD/MM/YYYY HH:mm',
    moment.ISO_8601,
  ]);

  return date.isValid() ? date.format('YYYY-MM-DDTHH:mm') : '';
};

export const calculateTotalDistanceMeters = (coordinates = []) => {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return 0;

  const earthRadius = 6371000;
  let totalDistance = 0;

  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const [lat1, lng1] = coordinates[index];
    const [lat2, lng2] = coordinates[index + 1];

    const toRadians = value => (value * Math.PI) / 180;
    const dLat = toRadians(lat2 - lat1);
    const dLng = toRadians(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    totalDistance += earthRadius * c;
  }

  return Math.round(totalDistance);
};

export const getRouteWaypointsFromPoints = (pointsList = []) => {
  const routeWaypoints = (pointsList || [])
    .map((point, index) => {
      if (point?.type?.id !== 'via' && point?.action?.id !== 'via') return null;

      const lat = Number(point?.lat);
      const lng = Number(point?.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

      return {
        lat,
        lng,
        index,
        identifier: point?.identifier,
      };
    })
    .filter(Boolean);

  if (routeWaypoints.length === 0) {
    return null;
  }

  return JSON.stringify(routeWaypoints);
};

export const hasMissingRequiredFields = (point) => {
  if (!point) return true;
  if (!point?.address || !point?.country) return true;
  if (!point?.city?.name) return true;
  return false;
};

export const hasInvalidDateFields = (point) => {
  const start = point?.start_schedule?.toString()?.toLowerCase();
  const end = point?.scheduled_time?.toString()?.toLowerCase();

  return !start || !end || start.includes('invalida') || end.includes('invalida');
};

export const getMissingFieldWarnings = (pointsList = []) => {
  const warnings = [];

  pointsList.forEach((point, index) => {
    const number = index + 1;
    const typeLabel = point?.type?.name?.toUpperCase() || 'PONTO';
    const hasCityObject = point?.city && typeof point.city === 'object';
    const hasCity = hasCityObject ? Boolean(point?.city?.name) : Boolean(point?.city);

    if (!point?.address) {
      warnings.push(`O logradouro da ${typeLabel} #${number} não está preenchido.`);
    }

    if (!point?.country) {
      warnings.push(`O país da ${typeLabel} #${number} não está preenchido.`);
    }

    if (!hasCity) {
      warnings.push(`A cidade da ${typeLabel} #${number} não está preenchida.`);
    }
  });

  return warnings;
};

const hasPointExecutionData = (point) => Boolean(
  point?.arrivalTime ||
  point?.arrival_time ||
  point?.effectiveTime ||
  point?.effective_time,
);

export const canEditPointByRoute = (point, canEditRoute) => {
  if (!canEditRoute) return false;
  if (!point) return canEditRoute;

  return !hasPointExecutionData(point);
};

export const getBlockedPoints = (visiblePoints = []) =>
  (visiblePoints || []).filter(hasPointExecutionData);

export const getHasAnyEditablePoint = (visiblePoints = []) =>
  (visiblePoints || []).some(point => !hasPointExecutionData(point));

const getAlphabeticPointLabel = (index) => {
  let current = Number(index);
  if (!Number.isInteger(current) || current < 0) return 'A';

  let label = '';
  while (current >= 0) {
    label = String.fromCharCode((current % 26) + 65) + label;
    current = Math.floor(current / 26) - 1;
  }

  return label;
};

export const getBlockedText = (blockedPoints = [], visiblePoints = []) => {
  if (!blockedPoints.length) return '';

  return blockedPoints
    .map((point, index) => {
      const visibleIndex = visiblePoints.findIndex(
        p => p?.identifier === point?.identifier,
      );
      const letterIndex = visibleIndex >= 0 ? visibleIndex : index;
      return `Ponto ${getAlphabeticPointLabel(letterIndex)}`;
    })
    .join(', ');
};

export const getConsolidatedWarnings = ({
  showStatusBlockWarning,
  isBlockedByRisk,
  visiblePoints,
  hasRouterError,
  hasNoRouteInRegion,
}) => {
  const warnings = [];

  if (showStatusBlockWarning && !isBlockedByRisk) {
    warnings.push('Status da viagem não permite edição da rota.');
  }

  const fieldWarnings = getMissingFieldWarnings(visiblePoints);
  if (fieldWarnings.length > 0) {
    warnings.push(...fieldWarnings);
  }

  const missingDateCount = (visiblePoints || []).filter(hasInvalidDateFields).length;
  if (missingDateCount > 0) {
    warnings.push(`Existem ${missingDateCount} ponto(s) com datas inválidas ou não preenchidas.`);
  }

  if (hasRouterError && !hasNoRouteInRegion) {
    warnings.push('Não foi possível calcular a rota para pelo menos um ponto. Ajuste o endereço e a posição no mapa sobre uma via.');
  }

  return warnings;
};

export const getWarningHeadline = (consolidatedWarnings = []) => {
  const riskMessage = consolidatedWarnings.find(warning => warning.includes('gerenciador de risco'));
  if (riskMessage) return riskMessage;

  const statusMessage = consolidatedWarnings.find(warning => warning.includes('Status da viagem'));
  if (statusMessage) return statusMessage;

  return 'Existem pontos com inconsistências. Passe o mouse para detalhes.';
};